import { TimeRange } from 'features/systemLens'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import type { ActiveSentinel } from 'features/timeSeries/hooks/useSentinelScores'
import {
  BaseSensorEntry,
  ChartTimeUnit,
  DataProcessingConfig,
  DateConfig,
  RegressionConfig,
  SmoothingConfig,
  SourceConfig,
  ThresholdFilterConfig,
  TimeSeriesDerivedValueEntryJSON,
  YAxisMode,
} from 'types'

const buildSourceConfig = (
  id: string,
  type: 'sensor' | 'derivedValue',
  colorIndex: number,
  customSensorColors: Record<string, string>,
  customAxisRanges: Record<string, { min?: number; max?: number }>,
  sensorVisibilityConfig: Record<string, boolean>,
  autoScaleMode: boolean
): SourceConfig => {
  let yAxisRange = null
  if (!autoScaleMode) {
    const axisRange = customAxisRanges[id]
    // An inverted pair (min > max) is never serialized: the chart schema
    // refines `max >= min`, so persisting one would poison every subsequent
    // save/local-state parse (`sanitizeInvertedYAxisRanges` would then delete
    // it on hydration and the user's bounds would silently vanish). Edits are
    // guarded upstream; this is the last belt before persistence.
    yAxisRange =
      axisRange &&
      axisRange.min !== undefined &&
      axisRange.max !== undefined &&
      axisRange.max >= axisRange.min
        ? { min: axisRange.min, max: axisRange.max }
        : null
  }

  return {
    type,
    sourceId: id,
    color: customSensorColors[id] || getSensorColor(colorIndex),
    hidden: sensorVisibilityConfig[id] === false,
    yAxisRange,
  }
}

export const buildSourceConfigsFromState = (
  selectedSensors: BaseSensorEntry[],
  customSensorColors: Record<string, string>,
  derivedValues: TimeSeriesDerivedValueEntryJSON[],
  customAxisRanges: Record<string, { min?: number; max?: number }>,
  sensorVisibilityConfig: Record<string, boolean>,
  autoScaleMode: boolean,
  // Required, not defaulted: this builder's output replaces `source_configs`
  // wholesale on save, so a caller that "forgot" sentinels would silently
  // strip them from the saved chart.
  activeSentinels: ActiveSentinel[],
  sentinelVisibilityConfig: Record<string, boolean>
): SourceConfig[] => {
  const sensorConfigs: SourceConfig[] = selectedSensors.map((se, sensorIndex) =>
    buildSourceConfig(
      se.id,
      'sensor',
      sensorIndex,
      customSensorColors,
      customAxisRanges,
      sensorVisibilityConfig,
      autoScaleMode
    )
  )

  const derivedConfigs: SourceConfig[] = derivedValues.map((dv, derivedIndex) =>
    buildSourceConfig(
      dv.id,
      'derivedValue',
      derivedIndex,
      customSensorColors,
      customAxisRanges,
      sensorVisibilityConfig,
      autoScaleMode
    )
  )

  /**
   * Sentinel overlays, keyed by their Rolodex entry id. The target PEN is
   * already serialized above as its own sensor config — the sentinel config
   * only records which sentinel bands which tag (plus chosen method and
   * overlay visibility), so loading one must not re-add the pen. Color
   * mirrors the target pen's (the overlay is drawn in the pen's color; see
   * `buildSentinelBandOverlays`), falling back deterministically when the
   * target pen has been removed from the chart.
   */
  const sentinelConfigs: SourceConfig[] = activeSentinels.map((sentinel) => ({
    type: 'sentinel',
    sourceId: sentinel.entryId,
    color:
      sensorConfigs.find((config) => config.sourceId === sentinel.target)
        ?.color ??
      customSensorColors[sentinel.target] ??
      getSensorColor(0),
    hidden: sentinelVisibilityConfig[sentinel.entryId] === false,
    yAxisRange: null,
    sentinelTarget: sentinel.target,
    ...(sentinel.name !== undefined && { sentinelName: sentinel.name }),
    ...(sentinel.method !== undefined && { sentinelMethod: sentinel.method }),
  }))

  return [...sensorConfigs, ...derivedConfigs, ...sentinelConfigs]
}

export const buildYAxisModeFromState = (singleScaleMode: boolean): YAxisMode =>
  singleScaleMode ? 'single' : 'multiple'

/**
 * Flatten the three separate live-state shapes that back the "Data Processing"
 * panel into the single `data_processing_configs` array the chart entry stores.
 *
 * Threshold filters and smoothing are keyed by sensor id in provider state;
 * regressions are already a list. The stored array is the union of all three,
 * discriminated by `type`. `loadDataProcessingConfigsFromChart` is the exact
 * inverse — keep the two in step or panel state vanishes on reload.
 */
export const buildDataProcessingConfigsFromState = (
  thresholdFilters: Record<string, ThresholdFilterConfig>,
  smoothingConfigs: Record<string, SmoothingConfig>,
  regressionConfigs: RegressionConfig[]
): DataProcessingConfig[] => [
  ...Object.values(thresholdFilters),
  ...Object.values(smoothingConfigs),
  ...regressionConfigs,
]

export const buildDateConfigFromState = (
  dateMode: 'absolute' | 'relative',
  timeRange: TimeRange,
  relativeStart?: { value?: number; unit: ChartTimeUnit },
  relativeEnd?: { isNow: boolean; value?: number; unit: ChartTimeUnit },
  cadenceSeconds: number = 10
): DateConfig => {
  if (dateMode === 'absolute') {
    return {
      mode: 'absolute',
      startDate: new Date(timeRange.from),
      endDate: new Date(timeRange.to),
    }
  }
  const start = {
    value: relativeStart?.value ?? 7,
    unit: relativeStart?.unit ?? 'days',
  }
  const end =
    !relativeEnd?.isNow && relativeEnd?.value !== undefined
      ? { value: relativeEnd.value!, unit: relativeEnd.unit ?? 'days' }
      : null

  return {
    mode: 'relative',
    startDateLookback: start,
    endDateLookback: end,
    updateCadenceSeconds: cadenceSeconds,
  }
}
